const INLINE_IDENTITY_KEYS = [
  'id',
  'pk',
  'hardware_id',
  'hardwareId',
  'companyPanelID',
  'companyPanelId',
  'companyInverterID',
  'companyInverterId'
];

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export function isCompanyComponentId(id: unknown): boolean {
  if (id === undefined || id === null || id === '') return false;
  if (typeof id === 'number') return false;
  return !/^\d+$/.test(String(id));
}

function normalizeHardwareIdentityValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeHardwareIdentityValue);
  }
  if (isObjectRecord(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, normalizeHardwareIdentityValue(nested)])
    );
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return String(value);
  }
  return value;
}

export function sanitizeInlineHardwareSpec(
  spec: unknown
): Record<string, unknown> | null {
  if (!isObjectRecord(spec)) return null;

  const sanitized = { ...spec };
  INLINE_IDENTITY_KEYS.forEach((key) => delete sanitized[key]);
  return Object.keys(sanitized).length ? sanitized : null;
}

export function buildEffectiveInlineHardwareSpec(
  defaultHardware: unknown,
  directSpec: unknown
): Record<string, unknown> | null {
  const sanitizedSpec = sanitizeInlineHardwareSpec(directSpec);
  if (!sanitizedSpec) return null;

  return sanitizeInlineHardwareSpec({
    ...(isObjectRecord(defaultHardware) ? defaultHardware : {}),
    ...sanitizedSpec
  });
}

export function buildHardwareSelectionKey({
  registeredId = null,
  spec = null,
  fallbackHardware = null,
  hardwareFkField = 'inverter'
}: {
  registeredId?: unknown;
  spec?: unknown;
  fallbackHardware?: unknown;
  hardwareFkField?: 'panel' | 'inverter';
} = {}): string {
  if (isCompanyComponentId(registeredId)) {
    return `id:${String(registeredId)}`;
  }
  if (isObjectRecord(fallbackHardware)) {
    if (isCompanyComponentId(fallbackHardware.id)) {
      return `id:${String(fallbackHardware.id)}`;
    }
    const nestedId = fallbackHardware[hardwareFkField];
    if (
      nestedId !== undefined &&
      nestedId !== null &&
      typeof nestedId !== 'object'
    ) {
      return `hardware:${String(nestedId)}`;
    }
  }
  const inlineSpec = sanitizeInlineHardwareSpec(spec ?? fallbackHardware);
  return inlineSpec
    ? `spec:${JSON.stringify(normalizeHardwareIdentityValue(inlineSpec))}`
    : 'none';
}

export function resolveSlotHardwareFromCanvas({
  spec = null,
  fkObject = null,
  idValue = null,
  catalog = []
}: {
  spec?: unknown;
  fkObject?: unknown;
  idValue?: unknown;
  catalog?: Array<{ id?: unknown }>;
} = {}): { hardware: unknown; source: 'spec' | 'object' | 'id' | 'none' } {
  if (spec) {
    const sanitizedSpec = sanitizeInlineHardwareSpec(spec);
    if (sanitizedSpec) {
      return { hardware: sanitizedSpec, source: 'spec' };
    }
  }
  if (fkObject) {
    return { hardware: fkObject, source: 'object' };
  }
  if (idValue !== undefined && idValue !== null) {
    const match = (catalog || []).find(
      (hardware) => String(hardware?.id) === String(idValue)
    );
    if (match) {
      return { hardware: match, source: 'id' };
    }
  }
  return { hardware: null, source: 'none' };
}

export function resolveProductionPackageFromCanvas({
  installationPackage = {},
  canvasData = null,
  panels = []
}: {
  installationPackage?: Record<string, any>;
  canvasData?: Record<string, any> | null;
  panels?: any[];
} = {}): Record<string, any> {
  const canvas = canvasData || {};
  const merged = {
    ...installationPackage,
    ...(isObjectRecord(canvas.package) ? canvas.package : {})
  };
  const hasSavedPanelSlot = Boolean(
    canvas.selected_panel_spec ||
      canvas.selected_panel ||
      (canvas.selected_panel_id !== undefined &&
        canvas.selected_panel_id !== null &&
        canvas.selected_panel_id !== '')
  );
  if (!hasSavedPanelSlot) return merged;

  const resolved = resolveSlotHardwareFromCanvas({
    spec: canvas.selected_panel_spec,
    fkObject: canvas.selected_panel,
    idValue: canvas.selected_panel_id,
    catalog: panels
  });
  return resolved.hardware ? { ...merged, panel: resolved.hardware } : merged;
}

export function resolveInstallationPackage(
  packages: any[] | null | undefined,
  leadState?: string | null
): Record<string, any> {
  const list = Array.isArray(packages) ? packages.filter(Boolean) : [];
  if (!list.length) return {};

  const stateCode =
    leadState != null && String(leadState).trim() !== ''
      ? String(leadState).trim()
      : null;
  const statePackage = stateCode
    ? list.find((item) => item?.state === stateCode)
    : undefined;
  const defaultPackage = list.find((item) => item?.state == null);

  return (statePackage ?? defaultPackage ?? list[0] ?? {}) as Record<
    string,
    any
  >;
}

export function findPrimaryInverterUsage(
  invertersUsed: unknown,
  inverter: unknown
): Record<string, unknown> | null {
  if (!Array.isArray(invertersUsed) || !isObjectRecord(inverter)) return null;

  if (inverter.id !== undefined && inverter.id !== null && inverter.id !== '') {
    return (
      (invertersUsed.find(
        (item: any) => String(item?.hardware_id) === String(inverter.id)
      ) as Record<string, unknown>) || null
    );
  }

  if (!inverter.model) return null;
  return (
    (invertersUsed.find(
      (item: any) => String(item?.model) === String(inverter.model)
    ) as Record<string, unknown>) || null
  );
}

export function buildSelectedPanelCommit(
  packageData: Record<string, any> = {},
  panel: Record<string, any> | null = null
) {
  if (!panel) {
    const selectedPanelId = packageData?.panel?.id;
    return {
      package: packageData,
      persistence: {
        selected_panel_id:
          selectedPanelId !== undefined && selectedPanelId !== null
            ? String(selectedPanelId)
            : null
      }
    };
  }

  const registered = panel.id !== undefined && panel.id !== null;
  const selectedPanelSpec = registered
    ? null
    : sanitizeInlineHardwareSpec(panel);
  return {
    package: {
      ...packageData,
      panel,
      pendingPanel: null,
      selectedPanelSpec
    },
    persistence: {
      selected_panel_id: registered ? String(panel.id) : null,
      selected_panel_spec: selectedPanelSpec
    }
  };
}
